import React from 'react'
import { Outlet } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { ProfileRootPageDocument } from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { observer } from 'mobx-react'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import users from 'modules/core/store/collections/users'

import RegularAccessBanner from 'modules/employees/components/blocks/RegularAccessBanner'
import { CommonRouteContext } from 'modules/employees/lib/types'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import CollectionsBootstrap from 'modules/profile/lib/CollectionsBootstrap'

export interface RouteContext extends CommonRouteContext {
  competenciesAvailable: boolean
  canSeePermissions: boolean
  canEditPersonalInfo: boolean
  permissionGroupName: string
  permissionGroupId: string
  canSeePerformance: boolean
  canSeeCustomDimensions: boolean
  seeOldCostCentersAssignments: boolean
  canSeeOverview: boolean
  moduleAvailabilities: ModuleAvailabilityType[]
}

const Container = observer(() => {
  const { company } = useCurrentSession()
  const employee = users.current.employee!
  const employeeId = employee.attributes.id
  const cardEmployeeRight = React.useRef<HTMLElement | null>(null)
  const headerTitle = React.useRef<HTMLElement | null>(null)

  const { data } = useQuery(ProfileRootPageDocument, {
    variables: { id: employeeId, companyId: company.id },
  })

  const employeeData = data?.employees.employee
  const features = keyBy(data?.features?.featuresConnection?.nodes, 'name')
  const canSeeCustomDimensions = features['dev_finance_show_custom_dimensions']?.enabled ?? false
  const seeOldCostCentersAssignments =
    features['see_old_cost_centers_assignments']?.enabled ?? false

  const canSeePersonalInfo = data?.employees.employee?._policies?.readPersonalInfo.can ?? false
  const canEditPersonalInfo =
    data?.employees.employee?.access.personalInfo?._policies?.edit.can ?? false
  // we are only showing the permission group in the employees page, but not in the self profile
  const canSeePermissions = false
  const permissionGroupName = ''
  const permissionGroupId = ''

  const employeeGraphqlPolicies = data?.employees.employee?._policies
  const canSeePerformance = employeeGraphqlPolicies?.seeEmployeePerformanceTab.can ?? false
  const canSeeFutureEmployees = data?.employees._policies?.seeFutureEmployees.can ?? false

  const {
    loading: loadingCompetenciesAvailability,
    featureAvailability: competenciesAvailability,
  } = useFeatureAvailability(Features.competencies_core)

  const { moduleAvailabilities } = useModuleAvailabilities()

  if (!employee) return <NotFound />

  if (loadingCompetenciesAvailability)
    return (
      <Box width="full" alignItems="center" height="full" justifyContent="center">
        <Spinner />
      </Box>
    )

  const context: RouteContext = {
    employee,
    competenciesAvailable: competenciesAvailability.available,
    cardEmployeeRight,
    headerTitle,
    canSeePersonalInfo,
    canEditPersonalInfo,
    canSeePermissions,
    permissionGroupName,
    permissionGroupId,
    canSeePerformance,
    canSeeCustomDimensions,
    seeOldCostCentersAssignments,
    canSeeOverview: true,
    moduleAvailabilities,
  }

  return (
    <ResponsivePage.Body variant="fullscreen" showHeader={false}>
      {employeeData && (
        <RegularAccessBanner
          employee={employeeData}
          canSeeFutureEmployees={canSeeFutureEmployees}
        />
      )}
      <Outlet context={context} />
    </ResponsivePage.Body>
  )
})

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
